import {Button} from '@dropbox/dig-components/buttons';
import {Menu} from '@dropbox/dig-components/menu';
import {Text} from '@dropbox/dig-components/typography';
import {Split} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CheckmarkLine} from '@dropbox/dig-icons/assets';
import {t} from 'i18next';
import {type ComponentProps, useMemo} from 'react';

import type {MetricsGrain} from './MetricsGrainControls';
import {
  buildMetricsQuarterOptions,
  buildMetricsYearOptions,
  parseMetricsTimeframeValue,
} from './metricsTimeframe';
import type {MetricQuarter} from './WorkstreamMetricsQuarterTable';

const GrainMenuTrigger = ({label, isSelected}: {label: string; isSelected: boolean}) => (
  <Split alignY="center" gap="8" style={{width: '100%'}}>
    <Split.Item width="fill">
      <Text>{label}</Text>
    </Split.Item>
    {isSelected ? (
      <Split.Item>
        <UIIcon src={CheckmarkLine} size="small" />
      </Split.Item>
    ) : null}
  </Split>
);

/**
 * Combined grain + timeframe menu for Metrics views. The trigger shows the active
 * selection ("Q3 2026" or "2026"). Inside the menu, Quarterly and Annually each
 * open a submenu of timeframe options with a checkmark on the current selection.
 */
export const MetricsTimeframeSelector = ({
  grain,
  quarter,
  year,
  onSelect,
  triggerVariant = 'opacity',
  triggerSize,
}: {
  grain: MetricsGrain;
  quarter: MetricQuarter;
  year: number;
  onSelect: (selection: {grain: MetricsGrain; quarter: MetricQuarter; year: number}) => void;
  triggerVariant?: ComponentProps<typeof Button>['variant'];
  triggerSize?: ComponentProps<typeof Button>['size'];
}) => {
  const quarterOptions = useMemo(() => buildMetricsQuarterOptions(), []);
  const yearOptions = useMemo(() => buildMetricsYearOptions(), []);
  const triggerLabel = grain === 'annual' ? String(year) : `${quarter} ${year}`;

  const handleQuarterSelect = (timeframe: string) => {
    const parsed = parseMetricsTimeframeValue(timeframe);
    if (!parsed) return;
    onSelect({grain: 'quarterly', quarter: parsed.quarter, year: parsed.year});
  };

  const handleYearSelect = (nextYear: number) => {
    onSelect({grain: 'annual', quarter, year: nextYear});
  };

  return (
    <Menu.Wrapper>
      {({getContentProps, getTriggerProps}) => (
        <>
          <Button
            {...getTriggerProps()}
            variant={triggerVariant}
            size={triggerSize}
            withDropdownIcon
          >
            {triggerLabel}
          </Button>
          <Menu.Content {...getContentProps()} placement="bottom-end" style={{minWidth: 200}}>
            <Menu.Segment withLabel={t('filter_timeframes')}>
              <Menu.Submenu
                withTriggerContent={
                  <GrainMenuTrigger label={t('quarterly')} isSelected={grain === 'quarterly'} />
                }
              >
                {quarterOptions.map((option) => {
                  const [optionYear, timeframes] = Object.entries(option)[0];
                  return (
                    <Menu.Segment key={optionYear}>
                      {timeframes.map((timeframe) => {
                        const parsed = parseMetricsTimeframeValue(timeframe);
                        if (!parsed) return null;
                        const isSelected =
                          grain === 'quarterly' &&
                          parsed.quarter === quarter &&
                          parsed.year === year;
                        return (
                          <Menu.ActionItem
                            key={timeframe}
                            onClick={() => handleQuarterSelect(timeframe)}
                            withRightAccessory={
                              isSelected ? <UIIcon src={CheckmarkLine} size="small" /> : undefined
                            }
                          >
                            {`${parsed.quarter} ${parsed.year}`}
                          </Menu.ActionItem>
                        );
                      })}
                    </Menu.Segment>
                  );
                })}
              </Menu.Submenu>
              <Menu.Submenu
                withTriggerContent={
                  <GrainMenuTrigger label={t('annual')} isSelected={grain === 'annual'} />
                }
              >
                <Menu.Segment>
                  {yearOptions.map((optionYear) => (
                    <Menu.ActionItem
                      key={optionYear}
                      onClick={() => handleYearSelect(optionYear)}
                      withRightAccessory={
                        grain === 'annual' && optionYear === year ? (
                          <UIIcon src={CheckmarkLine} size="small" />
                        ) : undefined
                      }
                    >
                      {String(optionYear)}
                    </Menu.ActionItem>
                  ))}
                </Menu.Segment>
              </Menu.Submenu>
            </Menu.Segment>
          </Menu.Content>
        </>
      )}
    </Menu.Wrapper>
  );
};
